import { omit } from 'lodash';
import { Reducer } from '@reduxjs/toolkit';

import { ResultsTypes, SportsType } from './types';

import { convertActionToPreviousStructure } from 'app/api';

import {
  fetchSports,
  getResultMatches,
  REMOVE_MATCHES_BY_SPORT_ID,
  RemoveMatchesBySportIdActionType,
  RESET_RESULTS_VALUES,
  ResetValuesActionType,
} from './actions';

export type ValuesStateType = Record<
  string,
  {
    loading: boolean;
    requested: boolean;
    error: string | null;
    sportName: string;
    payload: ResultsTypes;
  }
>;

export type ResultsState = {
  values: ValuesStateType;

  sports: SportsType | null;
  sportsLoading: boolean;
  sportsRequested: boolean;
  sportsError: string | null;
};

const initialState = {
  values: {},

  sports: null,
  sportsLoading: false,
  sportsRequested: false,
  sportsError: null,
};

type ActionType =
  | RemoveMatchesBySportIdActionType
  | ResetValuesActionType;

type ResultsReducerType = Reducer<ResultsState, ActionType>;

const resultsReducer: ResultsReducerType = (state = initialState, action) => {
  const convertedAction = convertActionToPreviousStructure(action);
  switch (action.type) {
    // --- SPORTS ---
    case fetchSports.pending.toString(): {
      return {
        ...state,
        sportsLoading: true,
        sportsRequested: false,
        sportsError: null,
      };
    }

    case fetchSports.fulfilled.toString(): {
      return {
        ...state,
        sportsLoading: false,
        sportsRequested: true,
        sportsError: null,
        sports: convertedAction.payload,
      };
    }

    case fetchSports.rejected.toString(): {
      return {
        ...state,
        sportsLoading: false,
        sportsRequested: true,
        sportsError: convertedAction.payload,
      };
    }

    // --- MATCHES ---
    case getResultMatches.pending.toString(): {
      return {
        ...state,
        values: {
          ...state.values,
          [convertedAction.meta.arg.sportId]: {
            loading: true,
            requested: false,
            error: null,
            sportName: convertedAction.meta.arg.sportName,
          },
        },
      };
    }

    case getResultMatches.fulfilled.toString(): {
      return {
        ...state,
        values: {
          ...state.values,
          [convertedAction.meta.arg.sportId]: {
            loading: false,
            requested: true,
            error: null,
            sportName: convertedAction.meta.arg.sportName,
            payload: convertedAction.payload,
          },
        },
      };
    }

    case getResultMatches.rejected.toString(): {
      return {
        ...state,
        values: {
          ...state.values,
          [convertedAction.meta.arg.sportId]: {
            loading: false,
            requested: true,
            error: convertedAction.payload,
            sportName: convertedAction.meta.arg.sportName,
          },
        },
      };
    }

    case REMOVE_MATCHES_BY_SPORT_ID: {
      return {
        ...state,
        values: omit(state.values, action.sportId),
      };
    }

    case RESET_RESULTS_VALUES: {
      return initialState;
    }

    default: {
      return state;
    }
  }
};

export default resultsReducer;
